import { useStore } from '@nanostores/react';

import { $brandWizardDraft, getBrandWizardDraft, type BrandWizardDraftPhase } from '../wizard/draft-store';

/**
 * The two reads the Styles page needs of the resume draft, kept apart because
 * they answer different questions:
 *
 * - `useHasResumableBrandRun` — "is there anything to resume here?", so the
 *   page mounts the wizard (and its banner) on load. Reactive, because a run
 *   started or discarded elsewhere in the session must move the page.
 * - `hasLiveBrandRun` — "is a generation still live?", so a Generate click can
 *   ask before replacing it. Terminal drafts (failed, expired) have nothing
 *   left to lose, so they never prompt.
 */

/** Phases with work still in flight or awaiting the user's review. */
const LIVE_PHASES: readonly BrandWizardDraftPhase[] = ['authoring', 'document_review'];

/** Whether a brand generation for this workspace is still being written or waiting for review. */
export function hasLiveBrandRun(workspaceId: number): boolean {
  const draft = getBrandWizardDraft(workspaceId);
  return draft != null && LIVE_PHASES.includes(draft.phase);
}

/**
 * Whether this workspace has any resumable brand generation, terminal outcomes
 * included. `authorRun` is required because that is exactly what the wizard's
 * own resume needs (`resumeDraft` bails without it): a draft with no authoring
 * run to resume would mount a wizard that immediately renders nothing.
 */
export function useHasResumableBrandRun(workspaceId: number | null): boolean {
  const draft = useStore($brandWizardDraft);
  return draft != null && workspaceId != null && draft.workspaceId === workspaceId && draft.authorRun != null;
}
